import * as React from 'react';
import {AreaRecorte} from './imageCrop';
import {
  ALVO,
  AppAssetKind,
  carregarImagem,
  desenharRecorte,
  exportarEmTamanhoAlvo,
  normalizarCor,
  precisaUpscale,
} from './appAssetCrop';

interface Entrada {
  arquivo: File;
  kind: AppAssetKind;
  corInicial?: string;
  onConfirm: (arquivo: File) => void;
  onCancel: () => void;
}
export const useAppAssetCropModal = ({
  arquivo,
  kind,
  corInicial,
  onConfirm,
  onCancel,
}: Entrada) => {
  const [posicao, setPosicao] = React.useState({x: 0, y: 0});
  const [zoom, setZoom] = React.useState(1);
  const [area, setArea] = React.useState<AreaRecorte | null>(null);
  const [margem, setMargem] = React.useState(0);
  const [cor, setCor] = React.useState(
    normalizarCor(kind === 'icon' ? '#ffffff' : corInicial),
  );
  const [erro, setErro] = React.useState<string | null>(null);
  const [processando, setProcessando] = React.useState(false);
  const [imagem, setImagem] = React.useState<HTMLImageElement | null>(null);
  const [urlOrigem, setUrlOrigem] = React.useState('');
  const [preview, setPreview] = React.useState('');
  const vivo = React.useRef(true);
  const ocupado = React.useRef(false);
  React.useEffect(() => {
    vivo.current = true;
    const url = URL.createObjectURL(arquivo);
    setUrlOrigem(url);
    carregarImagem(url)
      .then((resultado) => {
        if (vivo.current) setImagem(resultado);
      })
      .catch((falha: Error) => {
        if (vivo.current) setErro(falha.message);
      });
    return () => {
      vivo.current = false;
      URL.revokeObjectURL(url);
    };
  }, [arquivo]);
  React.useEffect(() => {
    if (!imagem || !area) return;
    const frame = requestAnimationFrame(() => {
      try {
        const canvas = document.createElement('canvas');
        desenharRecorte(canvas, imagem, area, {
          alvo: {largura: 320, altura: 320},
          margem,
          corDeFundo: cor,
        });
        setPreview(canvas.toDataURL('image/png'));
      } catch (falha) {
        setErro((falha as Error).message);
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [imagem, area, margem, cor]);
  const cancelar = () => {
    vivo.current = false;
    onCancel();
  };
  const confirmar = async () => {
    if (!area || !imagem || ocupado.current || !vivo.current) return;
    ocupado.current = true;
    setProcessando(true);
    setErro(null);
    try {
      const resultado = await exportarEmTamanhoAlvo(arquivo, area, {
        alvo: ALVO[kind],
        corDeFundo: cor,
        margem,
      });
      if (vivo.current) onConfirm(resultado);
    } catch (falha) {
      if (vivo.current)
        setErro((falha as Error).message || 'Não foi possível usar a imagem.');
    } finally {
      ocupado.current = false;
      if (vivo.current) setProcessando(false);
    }
  };
  return {
    posicao,
    setPosicao: (valor: Parameters<typeof setPosicao>[0]) => {
      if (!ocupado.current) setPosicao(valor);
    },
    zoom,
    setZoom: (valor: Parameters<typeof setZoom>[0]) => {
      if (!ocupado.current) setZoom(valor);
    },
    area,
    setArea: (valor: Parameters<typeof setArea>[0]) => {
      if (!ocupado.current) setArea(valor);
    },
    margem,
    setMargem: (valor: Parameters<typeof setMargem>[0]) => {
      if (!ocupado.current) setMargem(valor);
    },
    cor,
    setCor: (valor: Parameters<typeof setCor>[0]) => {
      if (!ocupado.current) setCor(valor);
    },
    erro,
    processando,
    urlOrigem,
    preview,
    cancelar,
    confirmar,
    pronto: Boolean(imagem && area),
    upscale: Boolean(
      area && precisaUpscale(area.width, area.height, ALVO[kind], margem),
    ),
  };
};
